import { isNotFoundError } from '@common/helpers/isNotFoundError';
import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import { SpaEntityType } from 'shared-types/Spa';

export const getSpaFacility = async (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  params: {
    arrivalDate: string | null;
    numberOfGuests?: number;
    showUnpublished?: number;
  }
) => {
  const { data } = await request.get(`${propertyId}/facilities/${spaFacilityId}/${language}`, {
    params,
  });
  return data as SpaEntityType;
};

export const getSpaFacilityPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  spaFacilityId: any,
  arrivalDate: string | null,
  showUnpublished = false,
  numberOfGuests?: number
) => {
  const key = getAvailableSpaFacilityKey(
    propertyId,
    language,
    spaFacilityId,
    {
      arrivalDate,
      numberOfGuests,
    },
    showUnpublished
  );

  try {
    // fetchQuery, not prefetchQuery: the caller has to be able to tell a facility
    // that is not there from one the API failed to hand over.
    return await queryClient.fetchQuery(
      key,
      () =>
        getSpaFacility(propertyId, language, spaFacilityId, {
          arrivalDate,
          ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
          ...(showUnpublished ? { showUnpublished: 1 } : {}),
        }),
      { cacheTime: Infinity, staleTime: Infinity, retry: false }
    );
  } catch (error) {
    if (isNotFoundError(error)) return undefined;

    throw error;
  }
};

export const useGetSpaFacility = (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  arrivalDate: string | null,
  enabled = true,
  numberOfGuests?: number
) => {
  const { data, isFetching, error } = useQuery({
    queryKey: getAvailableSpaFacilityKey(
      propertyId,
      language,
      spaFacilityId,
      {
        arrivalDate,
        numberOfGuests,
      },
      false
    ),
    queryFn: () =>
      getSpaFacility(propertyId, language, spaFacilityId, {
        arrivalDate,
        ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled,
  });
  return { data, isFetching, error };
};

export const getAvailableSpaFacilityKey = (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  params: {
    arrivalDate: string | null;
    numberOfGuests?: number;
  },
  showUnpublished: boolean
) => [
  'spaFacility',
  'available',
  propertyId,
  parseInt(spaFacilityId, 10),
  ...Object.values(params),
  language,
  showUnpublished,
];
